/**
 * Browser-side session management for React Router 7 SPA
 * Uses Better Auth for secure session management
 * Note: Despite the filename, this runs in the browser, not on a server
 */

import { redirect } from 'react-router';
import { authClient } from './auth-client';
import { apiClient } from './api-client';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

export interface SessionData {
  user: SessionUser | null;
}

/**
 * Get session data using Better Auth
 */
export async function getSession(): Promise<SessionData> {
  try {
    const { data } = await authClient.getSession();
    
    if (data?.user) {
      return {
        user: {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name || data.user.email.split('@')[0],
          role: 'user',
        },
      };
    }
  } catch (error) {
    console.error('Session verification failed:', error);
  }
  
  return { user: null };
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getSession();
  return !!session.user;
}

/**
 * Require authenticated session (use in components/hooks)
 */
export async function requireAuthenticated(): Promise<SessionData> {
  const session = await getSession();
  if (!session.user) {
    throw new Error('Authentication required');
  }
  return session;
}

/**
 * Require authenticated user or redirect to login
 */
export async function requireUser(
  request: Request,
  redirectTo: string = '/login'
): Promise<SessionUser> {
  const session = await getSession();
  
  if (!session.user) {
    const url = new URL(request.url);
    const redirectUrl = `${redirectTo}?from=${encodeURIComponent(url.pathname)}`;
    throw redirect(redirectUrl);
  }
  
  return session.user;
}

/**
 * Create login session using Better Auth
 */
export async function createUserSession(
  email: string,
  password: string,
  redirectTo: string = '/dashboard'
) {
  try {
    const { data, error } = await authClient.signIn.email({ email, password });
    
    if (error) {
      throw new Error(error.message || 'Login failed');
    }
    
    if (data) {
      return redirect(redirectTo);
    }
    
    throw new Error('Login failed');
  } catch (error) {
    throw error;
  }
}

/**
 * Destroy user session using Better Auth
 */
export async function destroyUserSession(redirectTo: string = '/login') {
  try {
    await authClient.signOut();
  } catch (error) {
    console.error('Logout error:', error);
  }
  
  return redirect(redirectTo);
}

/**
 * Helper to make authenticated API calls in loaders/actions
 * In browser context, cookies are automatically included with credentials: 'include'
 */
export function createAuthenticatedFetch() {
  return {
    get: <T = any>(path: string, options?: any) => 
      apiClient.get<T>(path, options),
    
    post: <T = any>(path: string, body?: any, options?: any) =>
      apiClient.post<T>(path, body, options),
    
    put: <T = any>(path: string, body?: any, options?: any) =>
      apiClient.put<T>(path, body, options),
    
    patch: <T = any>(path: string, body?: any, options?: any) =>
      apiClient.patch<T>(path, body, options),
    
    delete: <T = any>(path: string, options?: any) =>
      apiClient.delete<T>(path, options),
  };
}